import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { salesReportService, SalesTrendDTO } from '../../../../services/salesReportService';
import {
  TrendingUp, DollarSign, CheckCircle2, AlertTriangle,
  Receipt, BarChart2, Search, ArrowLeft,
} from 'lucide-react';
import './SalesReportCommon.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const today    = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

/** Format the ISO date string into a human-readable period label. */
const fmtPeriod = (iso: string, groupBy: 'day' | 'week' | 'month'): string => {
  try {
    const d = new Date(iso);
    if (groupBy === 'month')
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    if (groupBy === 'week')
      return `Wk ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

const SalesTrendsReportPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const fmt = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);

  // ── Filter state
  const [filters, setFilters] = useState({
    startDate: monthAgo(),
    endDate:   today(),
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('week');

  // ── Result state
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [trends,      setTrends]      = useState<SalesTrendDTO[]>([]);

  // ── Computed totals & chart max
  const totals = trends.reduce(
    (acc, t) => ({
      revenue:          acc.revenue          + t.totalSales,
      paid:             acc.paid             + t.paidAmount,
      unpaid:           acc.unpaid           + t.unpaidAmount,
      invoices:         acc.invoices         + t.numberOfInvoices,
      restaurantOrders: acc.restaurantOrders + t.numberOfRestaurantOrders,
    }),
    { revenue: 0, paid: 0, unpaid: 0, invoices: 0, restaurantOrders: 0 }
  );

  const maxRevenue = trends.length > 0 ? Math.max(...trends.map(t => t.totalSales)) : 1;
  const avgPerPeriod = trends.length > 0 ? totals.revenue / trends.length : 0;

  // ── Handlers
  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res  = await salesReportService.getSalesTrends({ ...filters, groupBy });
      const data: SalesTrendDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setTrends(data);
    } catch {
      setTrends([]);
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (field: string, value: string) =>
    setFilters(prev => ({ ...prev, [field]: value }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ssr-page">

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <div className="ssr-hero">
          <div>
            <h1 className="ssr-hero-title">
              <TrendingUp size={22} strokeWidth={1.8} />
              {t('salesReports.trendsTitle', 'Sales Trends')}
            </h1>
            <p className="ssr-hero-sub">
              {t('salesReports.trendsSubtitle', 'Revenue, paid and unpaid amounts grouped by day, week or month')}
            </p>
          </div>

          <div className="ssr-hero-actions">
            <button className="ssr-back-btn" onClick={() => navigate('/reports/sales')}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────── */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Search size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('salesReports.dateRangeFilter', 'Date Range Filter')}
            </span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="ssr-filter-bar">

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('common.startDate', 'From Date')}</span>
                <input
                  type="date"
                  className="ims-form-control"
                  value={filters.startDate}
                  onChange={e => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('common.endDate', 'To Date')}</span>
                <input
                  type="date"
                  className="ims-form-control"
                  value={filters.endDate}
                  onChange={e => handleFilterChange('endDate', e.target.value)}
                />
              </div>

              <div className="ssr-filter-group" style={{ maxWidth: 160 }}>
                <span className="ssr-filter-label">{t('salesReports.groupBy', 'Group by')}</span>
                <select
                  className="ims-form-control"
                  value={groupBy}
                  onChange={e => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
                >
                  <option value="day">{t('salesReports.daily', 'Daily')}</option>
                  <option value="week">{t('salesReports.weekly', 'Weekly')}</option>
                  <option value="month">{t('salesReports.monthly', 'Monthly')}</option>
                </select>
              </div>

              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={searching}
                style={{ alignSelf: 'flex-end' }}
              >
                {searching ? (
                  <><span className="ssr-spinner" /> {t('common.searching', 'Searching…')}</>
                ) : (
                  <><Search size={14} strokeWidth={2.5} /> {t('common.search', 'Search')}</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Initial prompt ────────────────────────────────────────── */}
        {!hasSearched && (
          <div className="ssr-prompt">
            <TrendingUp size={48} strokeWidth={1} />
            <p>
              {t('salesReports.searchPrompt', 'Set a date range and click')}{' '}
              <strong>{t('common.search', 'Search')}</strong>{' '}
              {t('salesReports.searchPromptTrendsEnd', 'to load sales trends.')}
            </p>
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────── */}
        {hasSearched && !searching && (
          <>
            {/* ── KPI row ──────────────────────────────────────────── */}
            {trends.length > 0 && (
              <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-total"><BarChart2 size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{trends.length}</span>
                    <span className="ims-stat-label">{t('salesReports.periods', 'Periods')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-sales"><DollarSign size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.revenue)}</span>
                    <span className="ims-stat-label">{t('salesReports.totalRevenue', 'Total Revenue')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-paid"><CheckCircle2 size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.paid)}</span>
                    <span className="ims-stat-label">{t('salesReports.amountPaid', 'Amount Paid')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-unpaid"><AlertTriangle size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.unpaid)}</span>
                    <span className="ims-stat-label">{t('salesReports.unpaid', 'Unpaid')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><Receipt size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{totals.invoices}</span>
                    <span className="ims-stat-label">{t('salesReports.totalInvoices', 'Total Invoices')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-avg"><TrendingUp size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(avgPerPeriod)}</span>
                    <span className="ims-stat-label">{t('salesReports.avgPerPeriod', 'Avg / Period')}</span>
                  </div>
                </div>

              </div>
            )}

            {/* ── Trend chart ───────────────────────────────────────── */}
            {trends.length === 0 ? (
              <div className="ims-card">
                <div className="ssr-empty">
                  {t('salesReports.noTrends', 'No sales data found for the selected period.')}
                </div>
              </div>
            ) : (
              <>
                <div className="ims-card">
                  <div className="ims-card-header">
                    <span className="ims-card-title">
                      <TrendingUp size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                      {t('salesReports.salesTrends', 'Sales Trends')}
                      <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                        — {t(`salesReports.${groupBy}ly`, groupBy === 'day' ? 'Daily' : groupBy === 'week' ? 'Weekly' : 'Monthly')}
                      </span>
                    </span>
                    {/* Legend */}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: 11, color: 'var(--ims-text-muted)', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--ims-raw-emerald-500)', display: 'inline-block' }} />
                        {t('salesReports.chartPaid', 'Paid')}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--ims-raw-red-400)', display: 'inline-block' }} />
                        {t('salesReports.chartUnpaid', 'Unpaid')}
                      </span>
                    </div>
                  </div>
                  <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
                    <div className="ssr-trend-list">
                      {trends.map((trend, idx) => {
                        const paidPct   = trend.totalSales > 0 ? (trend.paidAmount   / trend.totalSales) * 100 : 0;
                        const unpaidPct = trend.totalSales > 0 ? (trend.unpaidAmount / trend.totalSales) * 100 : 0;
                        const barWidth  = maxRevenue > 0 ? (trend.totalSales / maxRevenue) * 100 : 0;
                        return (
                          <div key={idx} className="ssr-trend-item">
                            <div className="ssr-trend-meta">
                              <span className="ssr-trend-label">
                                {fmtPeriod(trend.date, groupBy)}
                              </span>
                              <span className="ssr-trend-value">{fmt(trend.totalSales)}</span>
                            </div>
                            {/* Dual-segment bar: paid + unpaid share of total */}
                            <div className="ssr-trend-track" style={{ width: `${barWidth}%`, minWidth: '4px', background: 'var(--ims-border)' }}>
                              <div className="ssr-trend-dual" style={{ width: '100%' }}>
                                <div className="ssr-trend-dual-paid"   style={{ width: `${paidPct}%` }} />
                                <div className="ssr-trend-dual-unpaid" style={{ width: `${unpaidPct}%` }} />
                              </div>
                            </div>
                            <div className="ssr-trend-details">
                              <span>
                                {t('salesReports.paid', 'Paid')}: <strong>{fmt(trend.paidAmount)}</strong>
                              </span>
                              <span>
                                {t('salesReports.unpaid', 'Unpaid')}: <strong>{fmt(trend.unpaidAmount)}</strong>
                              </span>
                              <span>
                                {t('salesReports.invoices', 'Invoices')}: <strong>{trend.numberOfInvoices}</strong>
                              </span>
                              {trend.numberOfRestaurantOrders > 0 && (
                                <span>
                                  {t('salesReports.restaurantOrders', 'Restaurant Orders')}: <strong>{trend.numberOfRestaurantOrders}</strong>
                                  {' '}({fmt(trend.restaurantRevenue)})
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Data table ───────────────────────────────────── */}
                <div className="ims-card">
                  <div className="ims-card-header">
                    <span className="ims-card-title">
                      <BarChart2 size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                      {t('salesReports.trendsTable', 'Period Breakdown')}
                    </span>
                  </div>
                  <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    <table className="ssr-table">
                      <thead>
                        <tr>
                          <th>{t('salesReports.period', 'Period')}</th>
                          <th className="num">{t('salesReports.revenue', 'Revenue')}</th>
                          <th className="num">{t('salesReports.amountPaid', 'Paid')}</th>
                          <th className="num">{t('salesReports.unpaid', 'Unpaid')}</th>
                          <th className="num">{t('salesReports.invoices', 'Invoices')}</th>
                          <th className="num">{t('salesReports.avgInvoiceValue', 'Avg. Invoice')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trends.map((trend, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 600 }}>{fmtPeriod(trend.date, groupBy)}</td>
                            <td className="num ssr-revenue-value">{fmt(trend.totalSales)}</td>
                            <td className="num ssr-balance-zero">{fmt(trend.paidAmount)}</td>
                            <td className={`num ${trend.unpaidAmount > 0 ? 'ssr-balance-due' : 'ssr-balance-zero'}`}>
                              {fmt(trend.unpaidAmount)}
                            </td>
                            <td className="num">
                              {trend.numberOfInvoices}
                              {trend.numberOfRestaurantOrders > 0 && (
                                <span style={{ display: 'block', fontSize: 11, color: 'var(--ims-text-muted)' }}>
                                  +{trend.numberOfRestaurantOrders} {t('salesReports.restaurantOrdersLower', 'restaurant')}
                                </span>
                              )}
                            </td>
                            <td className="num">
                              {(() => {
                                const count = trend.numberOfInvoices + trend.numberOfRestaurantOrders;
                                return fmt(count > 0 ? trend.totalSales / count : 0);
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>
                            {t('salesReports.total', 'TOTAL')} ({trends.length}{' '}
                            {t('salesReports.periodsLower', 'periods')})
                          </td>
                          <td className="num">{fmt(totals.revenue)}</td>
                          <td className="num">{fmt(totals.paid)}</td>
                          <td className="num">{fmt(totals.unpaid)}</td>
                          <td className="num">
                            {totals.invoices}
                            {totals.restaurantOrders > 0 && (
                              <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--ims-text-muted)' }}>
                                +{totals.restaurantOrders} {t('salesReports.restaurantOrdersLower', 'restaurant')}
                              </span>
                            )}
                          </td>
                          <td className="num">{fmt(avgPerPeriod)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </Layout>
  );
};

export default SalesTrendsReportPage;
